import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Calculator = () => {
  const [currentIncome, setCurrentIncome] = useState(50000);
  const [targetIncome, setTargetIncome] = useState(1000000); // 10L
  const [avgTicket, setAvgTicket] = useState(10000);
  const [conversionRate, setConversionRate] = useState(5);

  const [results, setResults] = useState({
    leadsNeeded: 0,
    callsNeeded: 0,
    profitIncrease: 0,
    roi3Month: 0
  });

  useEffect(() => {
    const monthlyGap = targetIncome - currentIncome;
    const salesNeeded = Math.ceil(targetIncome / avgTicket);
    // Assuming 20% show rate for calls, and conversion rate is sales/leads? 
    // Let's simplify: Sales = Leads * (ConversionRate/100)
    // Leads = Sales / (ConversionRate/100)
    
    // Calls needed logic: 
    // Let's assume typical funnel: Leads -> Calls (20% of leads?) -> Sales (20% of calls?)
    // Or adhere to user prompt: "Calls needed (assume 20-30% show rate)"
    // Let's simpler model: 
    // Sales Needed = target / ticket
    // If conversion on call is ~20% (industry std), then calls needed = sales / 0.2
    // If show rate is 50%, then booked calls = calls_conducted / 0.5
    
    // User Prompt Logic: "Conversion rate estimate (1%-10%)" usually refers to Lead-to-Sale or Landing Page.
    // Let's use Lead-to-Sale for simplicity.
    const leadsNeeded = Math.ceil(salesNeeded / (conversionRate / 100));
    
    // Calls needed: Let's assume 20% of leads book a call? Or imply sales happen via call.
    // Let's say 1 Sale requires 5 Calls (20% closing).
    const callsNeeded = salesNeeded * 5; 

    const profitIncrease = targetIncome - currentIncome;
    const investment = 100000; // Diamond 2.0 anchor price
    const roi3Month = Math.round(((profitIncrease * 3) / investment) * 100);

    setResults({
      leadsNeeded: leadsNeeded > 0 ? leadsNeeded : 0,
      callsNeeded: callsNeeded > 0 ? callsNeeded : 0,
      profitIncrease: profitIncrease > 0 ? profitIncrease : 0,
      roi3Month: roi3Month > 0 ? roi3Month : 0
    });
  }, [currentIncome, targetIncome, avgTicket, conversionRate]);

  return (
    <div className="bg-slate-900 rounded-2xl p-6 md:p-10 border border-slate-800 shadow-xl">
      <div className="text-center mb-8">
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Potential ROI Calculator
        </h3>
        <p className="text-slate-400">See what's possible with the right system.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Current Monthly Income (₹)</label>
            <input 
              type="number" 
              value={currentIncome}
              onChange={(e) => setCurrentIncome(Number(e.target.value))}
              className="w-full bg-slate-800 text-white p-3 rounded-lg border border-slate-700 focus:border-amber-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Target Monthly Income (₹)</label>
            <input 
              type="number" 
              value={targetIncome}
              onChange={(e) => setTargetIncome(Number(e.target.value))}
              className="w-full bg-slate-800 text-white p-3 rounded-lg border border-slate-700 focus:border-amber-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Avg Sale Price (₹)</label>
            <select 
              value={avgTicket}
              onChange={(e) => setAvgTicket(Number(e.target.value))}
              className="w-full bg-slate-800 text-white p-3 rounded-lg border border-slate-700 focus:border-amber-500 outline-none"
            >
              <option value={5000}>₹5,000</option>
              <option value={10000}>₹10,000</option>
              <option value={30000}>₹30,000 (Coach)</option>
              <option value={50000}>₹50,000</option>
              <option value={100000}>₹1,00,000 (Consultant)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Est. Conversion Rate (%)</label>
            <input 
              type="range" 
              min="1" 
              max="10" 
              value={conversionRate}
              onChange={(e) => setConversionRate(Number(e.target.value))}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
            />
            <div className="text-right text-amber-500 font-bold">{conversionRate}%</div>
          </div>
        </div>

        {/* Results */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border border-slate-700 flex flex-col justify-center space-y-6">
          <div className="text-center">
            <p className="text-slate-400 text-sm uppercase tracking-wider">Monthly Profit Increase</p>
            <div className="text-3xl md:text-4xl font-bold text-green-400 mt-1">
              ₹{results.profitIncrease.toLocaleString('en-IN')}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <p className="text-xs text-slate-400 mb-1">Leads Needed</p>
              <p className="text-xl font-bold text-white">{results.leadsNeeded}</p>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <p className="text-xs text-slate-400 mb-1">Calls/Sales Needed</p>
              <p className="text-xl font-bold text-white">{results.callsNeeded}</p>
            </div>
          </div>

          <div className="text-center border-t border-slate-700 pt-4">
             <p className="text-slate-400 text-sm uppercase tracking-wider mb-1">3-Month ROI Potential</p>
             <div className="text-2xl font-bold text-amber-500">
               {results.roi3Month}%
             </div>
             <p className="text-xs text-slate-500 mt-2">*Based on implementation & effort.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;